"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createMedication,
  fetchMedications,
  fetchArchivedMedications,
  takeDose,
  toggleAlarm,
  archiveMedication,
  checkArchive,
  deleteMedication,
} from "../redux/thunks/medicationThunks";
import {
  selectMedicationState,
  selectActiveMedications,
  selectArchivedMedications,
} from "../redux/slices/medicationSlice";
import { useSocket } from "../../messagerie/context/SocketContext";

export default function useMedications(userId, { onReminder } = {}) {
  const dispatch = useDispatch();

  const medState = useSelector(selectMedicationState);
  const activeItems = useSelector(selectActiveMedications);
  const archivedItems = useSelector(selectArchivedMedications);

  const socketCtx = useSocket() || {};
  const socket = socketCtx.socket;
  const isSocketConnected = !!socketCtx.isConnected;

  const confirmTakenRaw = socketCtx.confirmTaken;
  const dismissReminderRaw = socketCtx.dismissReminder;

  const loadAll = useCallback(async () => {
    if (!userId) return;
    await dispatch(fetchMedications({ userId }));
  }, [dispatch, userId]);

  const loadArchived = useCallback(async () => {
    if (!userId) return;
    await dispatch(fetchArchivedMedications({ userId }));
  }, [dispatch, userId]);

  const addMedication = useCallback(
    async (payload) => {
      if (!userId) throw new Error("Missing userId");
      return dispatch(createMedication({ userId, payload }));
    },
    [dispatch, userId]
  );

  const markDoseTaken = useCallback(
    async (id, index) => {
      if (!userId) throw new Error("Missing userId");
      return dispatch(takeDose({ userId, id, index }));
    },
    [dispatch, userId]
  );

  const toggleReminder = useCallback(
    async (id) => {
      if (!userId) throw new Error("Missing userId");
      return dispatch(toggleAlarm({ userId, id }));
    },
    [dispatch, userId]
  );

  const archiveMed = useCallback(
    async (id) => {
      if (!userId) throw new Error("Missing userId");
      return dispatch(archiveMedication({ userId, id }));
    },
    [dispatch, userId]
  );

  const checkAutoArchive = useCallback(async () => {
    if (!userId) return;
    await dispatch(checkArchive({ userId }));
  }, [dispatch, userId]);

  const removeMedication = useCallback(
    async (id) => {
      if (!userId) throw new Error("Missing userId");
      return dispatch(deleteMedication({ userId, id }));
    },
    [dispatch, userId]
  );

  const confirmTaken = useCallback(
    async ({ medicationId, doseIndex }) => {
      if (typeof confirmTakenRaw === "function") {
        return confirmTakenRaw({ userId, medicationId, doseIndex });
      }

      if (socket?.connected) {
        socket.emit("confirm_taken", { userId, medicationId, doseIndex });
        return;
      }

      return dispatch(takeDose({ userId, id: medicationId, index: doseIndex }));
    },
    [confirmTakenRaw, socket, dispatch, userId]
  );

  const dismissReminder = useCallback(
    async ({ medicationId, doseIndex }) => {
      if (typeof dismissReminderRaw === "function") {
        return dismissReminderRaw({ userId, medicationId, doseIndex });
      }
      if (socket?.connected) {
        socket.emit("dismiss_reminder", { userId, medicationId, doseIndex });
      }
    },
    [dismissReminderRaw, socket, userId]
  );

  useReminderListener(onReminder);

  return {
      ...medState,

  items: activeItems?.length ? activeItems : medState.medications || [],
  archivedItems: archivedItems || [],

  loadAll,
  loadArchived,
  addMedication,
  markDoseTaken,
  toggleReminder,
  archiveMed,
  checkAutoArchive,
  removeMedication,

  isSocketConnected,
  confirmTaken,
  dismissReminder,
  };
}

export function useReminderListener(onReminder) {
  const { socket, isConnected } = useSocket() || {};
  const savedCb = useRef(onReminder);

  useEffect(() => {
    savedCb.current = onReminder;
  }, [onReminder]);

  useEffect(() => {
    if (!onReminder) return;

    if (!socket || typeof socket.on !== "function") {
      return;
    }

    const handler = (payload) => {
      try {
        savedCb.current?.(payload);
      } catch (e) {
      }
    };

    const attach = () => socket.on("medication_reminder", handler);
    const detach = () => socket.off("medication_reminder", handler);

    if (socket.connected || isConnected) {
      attach();
      return detach;
    }

    const onConnectOnce = () => attach();
    socket.once("connect", onConnectOnce);

    return () => {
      socket.off("connect", onConnectOnce);
      detach();
    };
  }, [socket, isConnected, onReminder]);
}
